import type React from 'react';
import { Chip } from '@heroui/react';
import type { LocaleCode, OperationCategory } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { categoryLabel } from '../../operationCategoryMeta';

type OperationCategoryChipProps = {
    /** `null` renders nothing — a system row has no category to show. */
    category: OperationCategory | null;
    locale: LocaleCode;
    /** Row discriminator, appended after the literal testid prefix. */
    slot: string;
};

/**
 * What kind of operation a row is.
 *
 * **Informational, and deliberately quieter than the status chip beside it.**
 * The status chip carries consequence — Active, Draft, Blocked decide whether an
 * operation can be recorded at all — and this one carries none: it is a label
 * the tenant chose, and two chips of equal weight on one row would make the
 * reader work out which of them matters. `tertiary` against the status chip's
 * filled treatment is the whole difference.
 *
 * **It never invents a category.** The set is the tenant's — seeded, then
 * theirs to rename, add to and remove — so there is no known member to fall back
 * on and no "Uncategorised" to show. A row without one renders no chip.
 */
export const OperationCategoryChip: React.FC<OperationCategoryChipProps> = ({
    category,
    locale,
    slot,
}) => {
    if (!category) {
        return null;
    }

    return (
        <Chip
            size="sm"
            variant="tertiary"
            {...getNodeTestIdProp(`operations-category-chip-${slot}`)}
        >
            <Chip.Label>{categoryLabel(category, locale)}</Chip.Label>
        </Chip>
    );
};
